import React, { useEffect, useRef } from "react";

import styled from "styled-components/macro";

import { FnrPopover } from "UI/FnrPopover2";
import { Button } from "UI/FnrSelectList3/Combobox/Components/Shared";

import { getDaysInMonth } from "Utilities/Date/getDaysInMonth";

import { useFnrDateInput2 } from "./FnrDatePicker2Context";
import useDayPickerKeyboardNavigation from "./useDayPickerKeyboardNavigation";

export default function DayPicker({ focusColor, disabled }) {
  const { state, setDay, setActiveField } = useFnrDateInput2();
  const { month, day, year, activeField } = state;
  const buttonRef = useRef();
  const dayButtonRefs = useRef([]);

  const daysInMonthArray = createDaysInMonthArray({ month, year });
  const { activeDay, handleKeyDown } = useDayPickerKeyboardNavigation({
    days: daysInMonthArray,
    isOpen: activeField === "day",
  });

  function toggleActiveField(e) {
    e.preventDefault();
    if (disabled) return;
    if (!month) return clickMonthButton();

    if (activeField === "day") {
      setActiveField("");
    } else {
      setActiveField("day");
    }
  }

  function clickMonthButton() {
    let parent = buttonRef.current.parentElement.parentElement;
    let monthButton = parent.querySelector(".month-picker");
    monthButton.click();
    setActiveField("month");
  }

  function handleDaySelection(day) {
    setDay(day);
    setActiveField("year");
  }

  function handleDayKeyDown(e, d) {
    if (e.key === "Enter") {
      handleDaySelection(activeDay);
      return;
    }
    if (e.key === "Escape") {
      setActiveField("");
      setTimeout(() => buttonRef.current.focus(), 100);
      return;
    }
    if (e.key === "Escape" || e.key === "Tab") {
      handleDaySelection(d);
      return;
    }
    handleKeyDown(e);
  }

  useEffect(
    function createArrayOfRefsForDayButtons() {
      if (!daysInMonthArray) return;

      dayButtonRefs.current = daysInMonthArray?.map((element, i) => {
        return dayButtonRefs?.current[i] ?? React.createRef();
      });
    },
    [daysInMonthArray]
  );

  useEffect(
    function focus() {
      if (!activeDay && activeField === "day") {
        buttonRef.current.focus();
        return;
      }

      if (activeDay) {
        dayButtonRefs.current[activeDay - 1].current.focus();
      }
    },
    [activeField, activeDay]
  );

  const style = {
    background: "transparent",
    border: "none",
  };

  return (
    <div data-cy="DayPicker_Wrapper" style={{ padding: "3px 3px" }}>
      <DayButton
        type="button"
        data-cy="DayPicker_DayButton"
        ref={buttonRef}
        onClick={toggleActiveField}
        style={style}
        focusColor={focusColor}
        onKeyDown={(e) => {
          if (activeField === "day" && !disabled) {
            handleKeyDown(e);
          }
        }}
        onBlur={() => {
          setTimeout(() => {
            if (!activeDay) {
              setActiveField("");
            }
          }, 200);
        }}
      >
        <Span
          className={`truncate`}
          lightText={!month && activeField !== "day"}
        >
          {day || "Day"}
        </Span>

        <Icon
          rotated={activeField === "day"}
          className="fas fa-chevron-down"
          color={focusColor}
          size="10px"
        />
      </DayButton>

      <FnrPopover
        buttonRef={buttonRef}
        show={activeField === "day"}
        onShouldClose={() => {
          setActiveField("");
        }}
      >
        <MonthDayPickerWrapper>
          {daysInMonthArray?.map((d, i) => (
            <MonthDay
              data-cy={`DayPicker_Day_${d}`}
              ref={dayButtonRefs.current[i]}
              key={d}
              onClick={() => handleDaySelection(d)}
              selected={day === d}
              active={activeDay === d}
              onKeyDown={(e) => handleDayKeyDown(e, d)}
            >
              {/* <HoverChild> */}
              <MonthDayInside>
                <MonthDayNumber>{d}</MonthDayNumber>
              </MonthDayInside>
              {/* </HoverChild> */}
            </MonthDay>
          ))}
        </MonthDayPickerWrapper>
      </FnrPopover>
    </div>
  );
}

const DayButton = styled(Button)`
  padding: 6px 10px;
  font-weight: 400;
  gap: 6px;
`;

const MonthDayPickerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  width: 240px;
`;

const MonthDay = styled.button`
  width: calc(100% / 7);
  flex-grow: 0;
  flex-shrink: 0;
  z-index: 2;
  text-align: center;
  cursor: pointer;
  position: relative;
  font-weight: ${(p) => (p.selected ? 600 : 400)};
  font-size: 1rem;
  padding: 2px;
  color: white;
  background: none;
  background-color: ${(p) => (p.selected ? "rgba(0,0,0,0.15)" : "none")};
  border: none;
  border-radius: 6px;
  outline: ${(p) => (p.active ? "1px solid white" : "none")};

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 6px;
  }
`;

const MonthDayInside = styled.div`
  padding-bottom: 100%;
`;

const MonthDayNumber = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Span = styled.span`
  color: #333;
  opacity: ${(props) => (props.lightText ? 0.7 : 1)};
  text-align: left;

  &:empty:before {
    content: "empty";
    opacity: 0;
  }
`;

const Icon = styled.i`
  font-size: ${(props) => props.size || "inherit"};
  rotate: ${(props) => (props.rotated ? "-180deg" : "0deg")};
  transition: rotate 0.2s ease-in-out;
  color: ${(props) => props.color || "inherit"};

  &.hide-icon {
    opacity: 0;
  }
`;

function createDaysInMonthArray({ month, year }) {
  if (!month) return;

  const daysInMonth = getDaysInMonth({ month, year });
  const daysArray = [];

  for (let i = 1; i <= daysInMonth; i++) {
    daysArray.push(i);
  }

  return daysArray;
}
